/**
 * Application-wide constants
 * 
 * These constants are used throughout the application to maintain consistency
 * and facilitate easier updates when content changes.
 */

import { FAQ_ITEMS } from './faq';

// Re-export FAQ items for backwards compatibility
export { FAQ_ITEMS };

/**
 * Features array defining all application features
 * Used by the Features component to display feature cards
 */
export const FEATURES = [
  {
    icon: 'bell',
    title: 'Smart Medication Reminders',
    description: 'Never miss a dose with customizable pill reminders and alerts. Set up multiple schedules for different medications.',
    delay: 'animate-fade-in-delay-1'
  },
  {
    icon: 'calendar',
    title: 'Medication Schedule Organizer',
    description: 'Organize your entire medication regimen in one place with an intuitive calendar interface and scheduling tools.',
    delay: 'animate-fade-in-delay-2'
  },
  {
    icon: 'chart',
    title: 'Comprehensive Pill Tracker',
    description: 'Track medication intake and adherence with detailed history logs and visual progress charts.',
    delay: 'animate-fade-in-delay-3'
  },
  {
    icon: 'clock',
    title: 'Refill Reminders',
    description: 'Get notified before your prescriptions run out. Never worry about running out of important medication.',
    delay: 'animate-fade-in-delay-1'
  },
  {
    icon: 'refresh',
    title: 'Medication Adherence Tools',
    description: 'Improve your medication compliance with tools designed to build consistent habits and track progress.',
    delay: 'animate-fade-in-delay-2'
  },
  {
    icon: 'users',
    title: 'Family Sharing',
    description: 'Share your medication schedule with family members or caregivers. Keep everyone in the loop.',
    delay: 'animate-fade-in-delay-3'
  },
  {
    icon: 'sparkles',
    title: 'Plus Exclusive Features',
    description: 'Advanced medication analytics, priority support, and more with Meduler Plus.',
    delay: 'animate-fade-in-delay-1'
  },
  {
    icon: 'shield',
    title: 'Private & Secure',
    description: 'Your medication data is encrypted and protected, ensuring your health information remains private.',
    delay: 'animate-fade-in-delay-2'
  },
  {
    icon: 'message',
    title: 'Caregiver Communication',
    description: 'Communicate with caregivers about medication changes and updates right within the app.',
    delay: 'animate-fade-in-delay-3'
  }
];

/**
 * How it works steps array
 * Used by the HowItWorks component to display step-by-step instructions
 */
export const HOW_IT_WORKS_STEPS = [
  {
    icon: 'check',
    title: 'Start for Free',
    description: 'Get started with our simple step-by-step process to access all essential medication management features.'
  },
  {
    icon: 'plus-circle',
    title: 'Add Your Medications',
    description: 'Enter your prescription details including name, dosage, frequency, and timing.'
  },
  {
    icon: 'bell',
    title: 'Set Reminders',
    description: 'Configure personalized reminders for each medication based on your schedule. Never miss a dose with timely notifications.'
  },
  {
    icon: 'users',
    title: 'Connect Caregivers',
    description: 'Optionally invite family members or caregivers to help monitor your medication schedule and receive alerts if doses are missed.'
  }
];

/**
 * Caregiver features array
 * Used by the Caregivers component to display caregiver-specific features
 */
export const CAREGIVER_FEATURES = [
  {
    icon: 'bell-ring',
    title: 'Missed Dose Alerts',
    description: 'Receive notifications when your loved one misses a scheduled medication dose.'
  },
  {
    icon: 'shield',
    title: 'Secure Medication Monitoring',
    description: 'Track adherence and medication history while respecting privacy.'
  },
  {
    icon: 'users',
    title: 'Multi-User Management',
    description: 'Coordinate care with other family members to ensure consistent medication support.'
  },
  {
    icon: 'refresh-cw',
    title: 'Refill Management',
    description: 'Get notified when prescriptions need refilling before they run out.'
  }
];
